// // src/components/ui/PageTransition.jsx
// // Drop-in page transition: gold top-bar progress + content fade
// // Usage: wrap <PageTransition> around your page <Routes> in App.jsx

// import { useState, useEffect, useRef } from 'react';
// import { useLocation } from 'react-router-dom';

// export default function PageTransition({ children }) {
//   const location = useLocation();
//   const [progress, setProgress]   = useState(0);
//   const [barVisible, setBarVisible] = useState(false);
//   const [fading, setFading]        = useState(false);   // fade-out old page
//   const [key, setKey]              = useState(location.pathname);
//   const timerRef = useRef(null);

//   useEffect(() => {
//     // 1. Start bar + fade out old content
//     setBarVisible(true);
//     setProgress(0);
//     setFading(true);

//     // 2. Race the bar to ~85% quickly
//     let prog = 0;
//     clearInterval(timerRef.current);
//     timerRef.current = setInterval(() => {
//       prog += Math.random() * 18 + 8;          // random increments feel real
//       if (prog >= 85) { prog = 85; clearInterval(timerRef.current); }
//       setProgress(prog);
//     }, 80);

//     // 3. After short delay: swap page content + finish bar
//     const swapTimer = setTimeout(() => {
//       setKey(location.pathname + location.search);
//       setFading(false);

//       clearInterval(timerRef.current);
//       setProgress(100);

//       // 4. Hide bar after it completes
//       setTimeout(() => setBarVisible(false), 400);
//     }, 260);   // ← tune this: shorter = snappier, longer = more dramatic

//     return () => {
//       clearInterval(timerRef.current);
//       clearTimeout(swapTimer);
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [location.pathname, location.search]);

//   return (
//     <>
//       {/* ── Gold progress bar ── */}
//       {barVisible && (
//         <div style={{
//           position: 'fixed',
//           top: 0, left: 0,
//           width: `${progress}%`,
//           height: 2,
//           background: 'linear-gradient(90deg, #C9A84C, #e8c96a, #C9A84C)',
//           zIndex: 99999,
//           transition: progress === 100
//             ? 'width 0.25s ease-out, opacity 0.35s ease 0.05s'
//             : 'width 0.18s ease-out',
//           opacity: progress === 100 ? 0 : 1,
//           pointerEvents: 'none',
//           boxShadow: '0 0 8px rgba(201,168,76,0.6)',
//         }} />
//       )}

//       {/* ── Page content with fade ── */}
//       <div
//         key={key}
//         style={{
//           opacity: fading ? 0 : 1,
//           transform: fading ? 'translateY(6px)' : 'translateY(0)',
//           transition: fading
//             ? 'opacity 0.18s ease, transform 0.18s ease'
//             : 'opacity 0.28s ease, transform 0.28s ease',
//         }}
//       >
//         {children}
//       </div>
//     </>
//   );
// }

// src/components/ui/PageTransition.jsx
// Skeleton screen page transition — shows animated placeholder shapes
// while the new page mounts, then fades into real content.

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/* ── Shimmer keyframe (injected once) ── */
const SHIMMER_STYLE = `
  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position:  600px 0; }
  }
  .sk-shimmer {
    background: linear-gradient(90deg, #ece8e1 25%, #f5f2ee 50%, #ece8e1 75%);
    background-size: 600px 100%;
    animation: shimmer 1.4s infinite linear;
  }
`;

/* ── Individual skeleton block ── */
function Sk({ w = '100%', h = 16, radius = 0, style = {} }) {
  return (
    <div
      className="sk-shimmer"
      style={{ width: w, height: h, borderRadius: radius, flexShrink: 0, ...style }}
    />
  );
}

/* ── Generic page skeleton ── */
function PageSkeleton() {
  return (
    <div style={{ padding: '48px 40px', maxWidth: '100%', background: '#fff', minHeight: '70vh' }}>

      {/* Hero banner placeholder */}
      <Sk h={320} radius={0} style={{ marginBottom: 48 }} />

      {/* Section heading */}
      <Sk w="18%" h={10} style={{ marginBottom: 10 }} />
      <Sk w="32%" h={28} style={{ marginBottom: 40 }} />

      {/* Product grid — 4 cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24, marginBottom: 56 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Sk h={260} />
            <Sk w="60%" h={11} style={{ margin: '0 auto' }} />
            <Sk w="40%" h={11} style={{ margin: '0 auto' }} />
          </div>
        ))}
      </div>

      {/* Text block */}
      <Sk w="22%" h={10} style={{ marginBottom: 10 }} />
      <Sk w="40%" h={24} style={{ marginBottom: 20 }} />
      <Sk w="90%" h={11} style={{ marginBottom: 8 }} />
      <Sk w="75%" h={11} style={{ marginBottom: 8 }} />
      <Sk w="55%" h={11} />
    </div>
  );
}

/* ── Main transition wrapper ── */
export default function PageTransition({ children }) {
  const location = useLocation();
  const [displayKey, setDisplayKey]     = useState(location.key);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [contentReady, setContentReady] = useState(true);

  useEffect(() => {
    // 1. Immediately show skeleton
    setShowSkeleton(true);
    setContentReady(false);

    // 2. After brief delay, swap in real page content
    const swap = setTimeout(() => {
      setDisplayKey(location.key);
      setShowSkeleton(false);

      // 3. Tiny extra tick so the new DOM paints before fade-in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setContentReady(true));
      });
    }, 420); // ← skeleton visible duration (ms) — tune to taste

    return () => clearTimeout(swap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return (
    <>
      <style>{SHIMMER_STYLE}</style>

      {/* Skeleton layer */}
      {showSkeleton && (
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          background: '#fff',
          animation: 'none',
        }}>
          <PageSkeleton />
        </div>
      )}

      {/* Real page content */}
      <div
        key={displayKey}
        style={{
          opacity:    contentReady ? 1 : 0,
          transition: 'opacity 0.35s ease',
        }}
      >
        {children}
      </div>
    </>
  );
}